import React from "react";

import { swingAPI, RobinhoodImport, SwingScheduleStatus } from "./api";
import { useRequestSwingRefresh, useSwingMLStatus, useSwingRefreshStatus } from "./queries";
import { StatePanel, SwingWorkspaceShell } from "./components/workspace-shell";
import { PublicationOperations } from "./news/screens/news-screen";
import { ConfirmationDialog } from "./news/components/confirmation-dialog";

const formatDate = (value?: string | null): string => value ? new Date(value).toLocaleString() : "N/A";
const money = (value?: number | null): string => value == null ? "—" : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
const activeState = (state?: string): boolean => state === "running" || state === "queued";
const formatState = (value?: string): string => value ? value.replaceAll("-", " ").replace(/\b\w/g, (character) => character.toUpperCase()) : "Checking";

export default function OperatorScreen(): JSX.Element {
  const ml = useSwingMLStatus();
  const refresh = useSwingRefreshStatus();
  const request = useRequestSwingRefresh();
  const [batch, setBatch] = React.useState<RobinhoodImport | null>(null);
  const [importError, setImportError] = React.useState("");
  const [confirmBackfill, setConfirmBackfill] = React.useState(false);

  const status = refresh.data;
  const manualActive = activeState(status?.state);
  const premarketActive = activeState(status?.premarket.state);
  const afterCloseActive = activeState(status?.afterClose.state);
  const refreshActive = request.isPending || manualActive || premarketActive || afterCloseActive;
  const lockConflict = status?.state === "failed" && status.error?.includes("refresh is already running");
  const activity = refreshActivity(status?.state, status?.session, status?.message, premarketActive, afterCloseActive, request.isPending);
  const collectionFailures = [
    scheduleFailure("Morning collection", "Updates Focus symbols with quotes and premarket indications.", status?.premarket),
    scheduleFailure("Close collection", "Runs as part of the full weekday Investments, Economics, and Swing maintenance job.", status?.afterClose),
  ].filter((failure): failure is ScheduleFailure => failure !== null);

  async function preview(file?: File): Promise<void> {
    if (!file) return;
    try {
      setImportError("");
      setBatch(await swingAPI.previewRobinhood(file));
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Import failed");
    }
  }

  async function confirm(): Promise<void> {
    if (!batch) return;
    try {
      setImportError("");
      setBatch(await swingAPI.confirmRobinhood(batch.id));
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Confirmation failed");
    }
  }

  return (
    <SwingWorkspaceShell title="Operator" description="Data collection, publication, broker reconciliation, strategy, and model readiness without crowding the daily desk.">
      <section className="swing-summary">
        <CollectionSummary label="Morning collection" status={status?.premarket} />
        <CollectionSummary label="Close collection" status={status?.afterClose} />
        <article><span>Strategy</span><strong>Balanced v1</strong><small>Deterministic setup rules</small></article>
        <article><span>ML</span><strong>{formatState(ml.data?.state)}</strong><small>Context only; never controls readiness</small></article>
      </section>

      {collectionFailures.length > 0 && (
        <section className="swing-collection-issues" aria-labelledby="collection-issues-heading">
          <header>
            <div>
              <span className="section-eyebrow">Scheduled collection issues</span>
              <h2 id="collection-issues-heading">Why a collection failed</h2>
              <p>These scheduled slots are independent from manual refreshes. A successful universe backfill does not clear an earlier morning or close failure.</p>
            </div>
          </header>
          <div>
            {collectionFailures.map((failure) => <CollectionFailureCard key={failure.label} failure={failure} />)}
          </div>
          <p className="swing-collection-log-help">If the provider message is still unclear, inspect the worker log with <code>docker logs --tail 200 personal-site-refresh</code>.</p>
        </section>
      )}

      <section className="swing-section">
        <header>
          <div>
            <span className="section-eyebrow">Market data refresh</span>
            <h2>{refreshActive ? activity.title : formatState(status?.state || "Checking worker")}</h2>
            <p>Routine market data refreshes run automatically. Manual requests are only for recovery or an intentional out-of-cycle update.</p>
          </div>
        </header>

        <div className="swing-operator-actions" aria-label="Manual market data refresh actions">
          <button className="swing-button secondary" disabled={refreshActive} onClick={() => request.mutate("premarket")}>Request premarket</button>
          <button className="swing-button" disabled={refreshActive} onClick={() => request.mutate("after-close")}>Request completed bars</button>
          <button className="swing-button secondary" disabled={refreshActive} onClick={() => setConfirmBackfill(true)}>Backfill stock universe</button>
        </div>

        {refreshActive && !status?.universe && (
          <div className="swing-refresh-activity" role="status" aria-live="polite">
            <div><strong>{activity.title}</strong><span>{activity.session}</span></div>
            <progress aria-label="Refresh activity in progress" />
            <small>{activity.message}</small>
          </div>
        )}

        {status?.startedAt && (
          <p className="swing-refresh-timestamps">
            Started {formatDate(status.startedAt)}
            {status.completedAt ? ` · completed ${formatDate(status.completedAt)}` : " · still active"}
          </p>
        )}

        {status?.universe && (
          <div className="swing-backfill-progress">
            <div>
              <strong>{status.universe.completed}/{status.universe.total} symbols</strong>
              <span>{status.universe.requests} requests · {(status.universe.bytes / 1024 / 1024).toFixed(1)} MB normalized payload · current {status.universe.ticker || "validation"}</span>
            </div>
            <progress aria-label="Universe collection progress" value={status.universe.completed} max={Math.max(1, status.universe.total)} />
            <small>Elapsed {Math.round((status.elapsedSeconds || 0) / 60)} min · estimated remaining {Math.round((status.estimatedRemainingSeconds || 0) / 60)} min · {status.universe.failures.length} failures</small>
          </div>
        )}

        {status?.coverage && <p className="swing-coverage-summary"><strong>{status.coverage.analyzed}/{status.coverage.total}</strong> analyzed · {status.coverage.stale} stale · {status.coverage.unavailable} unavailable · {status.coverage.missingBackfill} missing backfill</p>}

        {lockConflict && (
          <StatePanel tone="warning" title="Another refresh was already active">
            This older request did not start because scheduled maintenance owned the shared cache lock. New requests wait automatically; routine completed-bar data is already included in the weekday maintenance run.
          </StatePanel>
        )}
        {status?.error && !lockConflict && <StatePanel tone="error" title="Refresh failed">{status.error}</StatePanel>}
      </section>

      <section className="swing-section">
        <header>
          <div><span className="section-eyebrow">Robinhood reconciliation</span><h2>Import an activity report</h2><p>Compare brokerage activity with the decisions recorded in this workspace without connecting your Robinhood account.</p></div>
        </header>
        <div className="swing-reconciliation-intro">
          <div>
            <strong>What reconciliation does</strong>
            <p>Export an activity CSV from Robinhood, then preview how its buys, sells, dividends, splits, fees, and cancellations are understood. Confirmation stores recognized rows as idempotent audit events.</p>
            <ul>
              <li>The raw CSV is not retained.</li>
              <li>Unsupported rows remain unresolved and block confirmation.</li>
              <li>This does not contact Robinhood, place orders, or silently modify a plan or trade.</li>
            </ul>
          </div>
          <div className="swing-file-action">
            <span>Start with a Robinhood account activity export</span>
            <label className="swing-button secondary">Choose activity CSV<input hidden type="file" accept=".csv,text/csv" onChange={(event) => void preview(event.target.files?.[0])} /></label>
            <small>Maximum file size: 5 MB</small>
          </div>
        </div>
        {importError && <StatePanel tone="error" title="Import preview failed">{importError}</StatePanel>}
        {batch && (
          <div className="swing-import-preview">
            <strong>{batch.recognized} recognized · {batch.unresolved} unresolved</strong>
            {batch.rows.slice(0, 20).map((row) => <div key={row.row}><span>{row.date || "No date"}</span><b>{row.ticker || row.type}</b><span>{row.side || row.type}</span><span>{row.quantity ?? "—"} @ {money(row.price)}</span><em>{row.state}</em></div>)}
            <button className="swing-button" disabled={batch.state === "confirmed" || batch.unresolved > 0} onClick={() => void confirm()}>{batch.state === "confirmed" ? "Confirmed" : "Confirm recognized activity"}</button>
            {batch.unresolved > 0 && <p>Resolve or add parser support for unknown rows before confirmation.</p>}
          </div>
        )}
      </section>

      <section className="swing-section">
        <header><div><span className="section-eyebrow">Private news publication</span><h2>Validated bundle controls</h2><p>Remote checks and downloads happen only here. Activated bundles remain local and survive container restarts.</p></div></header>
        <PublicationOperations />
      </section>

      <StatePanel tone="warning" title="Production gates remain visible">After-close feasibility acceptance, an approved redacted Robinhood fixture, ML artifacts, and the 30-day shadow threshold remain gated. The product abstains instead of fabricating readiness.</StatePanel>
      <ConfirmationDialog
        open={confirmBackfill}
        title="Backfill the stock universe?"
        description={<>This makes one five-year adjusted-history request for every configured equity plus SPY. It checkpoints each completed symbol, so a stopped run can resume.</>}
        eyebrow="Universe collection"
        note="The worker stages and validates all completed data before replacing the active scanner cache."
        confirmLabel="Start backfill"
        pendingLabel="Starting backfill…"
        pending={request.isPending}
        onCancel={() => setConfirmBackfill(false)}
        onConfirm={() => request.mutate("universe-backfill", { onSuccess: () => setConfirmBackfill(false) })}
      />
    </SwingWorkspaceShell>
  );
}

type ScheduleFailure = {
  label: string;
  description: string;
  reasons: string[];
  guidance: string;
  status: SwingScheduleStatus;
};

function CollectionSummary({ label, status }: { label: string; status?: SwingScheduleStatus }): JSX.Element {
  const state = status?.state || "checking";
  return (
    <article className={`swing-collection-summary swing-collection-summary--${state}`}>
      <span>{label}</span>
      <strong>{formatState(state)}</strong>
      {status?.lastSuccessfulAt && <small>Last success {formatDate(status.lastSuccessfulAt)}</small>}
      <small>Next {formatDate(status?.nextScheduledAt)}</small>
    </article>
  );
}

function CollectionFailureCard({ failure }: { failure: ScheduleFailure }): JSX.Element {
  const handledAt = failure.status.lastHandledScheduledFor || failure.status.currentScheduledFor || failure.status.startedAt;
  return (
    <article>
      <header>
        <div><strong>{failure.label}</strong><span className="swing-status swing-status--invalidated">Failed</span></div>
        <p>{failure.description}</p>
      </header>
      <div className="swing-collection-reason">
        <span>{failure.reasons.length === 1 ? "Reported reason" : "Reported reasons"}</span>
        {failure.reasons.length === 1
          ? <strong>{failure.reasons[0]}</strong>
          : <ul>{failure.reasons.slice(0, 5).map((reason) => <li key={reason}>{reason}</li>)}</ul>}
        {failure.reasons.length > 5 && <small>{failure.reasons.length - 5} additional errors are available in the worker status file.</small>}
      </div>
      <p>{failure.guidance}</p>
      <dl>
        <div><dt>Scheduled slot</dt><dd>{formatDate(handledAt)}</dd></div>
        {failure.status.attempt ? <div><dt>Attempts</dt><dd>{failure.status.attempt}</dd></div> : null}
        {failure.status.lastSuccessfulAt ? <div><dt>Last success</dt><dd>{formatDate(failure.status.lastSuccessfulAt)}</dd></div> : null}
        <div><dt>Next automatic run</dt><dd>{formatDate(failure.status.nextScheduledAt)}</dd></div>
      </dl>
    </article>
  );
}

function scheduleFailure(label: string, description: string, status?: SwingScheduleStatus): ScheduleFailure | null {
  if (!status || status.state !== "failed") return null;
  const reasons = summarizeScheduleReasons([...new Set([status.error, ...(status.errors || [])].filter((value): value is string => Boolean(value?.trim())))]);
  if (reasons.length === 0) reasons.push(status.message || "The worker did not publish a specific failure reason.");
  return { label, description, reasons, guidance: collectionFailureGuidance(reasons[0]), status };
}

function summarizeScheduleReasons(reasons: string[]): string[] {
  const canceled = reasons.filter((reason) => reason.toLowerCase().includes("context canceled"));
  const remaining = reasons.filter((reason) => !reason.toLowerCase().includes("context canceled"));
  if (canceled.length === 1) remaining.push(canceled[0]);
  if (canceled.length > 1) remaining.push(`The refresh was canceled once; ${canceled.length.toLocaleString()} ticker operations inherited that canceled context.`);
  return remaining;
}

function collectionFailureGuidance(reason: string): string {
  const normalized = reason.toLowerCase();
  if (normalized.includes("already running") || normalized.includes("lock")) return "Another refresh owned the shared cache lock. Wait for that run to finish; the next scheduled slot will run normally, or you can request an out-of-cycle refresh afterward.";
  if (normalized.includes("context canceled") || normalized.includes("refresh was canceled")) return "This usually means the refresh container stopped or was redeployed while the slot was running; it does not mean every listed ticker failed independently. The next run can resume with a fresh attempt.";
  if (normalized.includes("timeout") || normalized.includes("deadline") || normalized.includes("429") || normalized.includes("502") || normalized.includes("503") || normalized.includes("504")) return "This looks transient. The worker already exhausted its in-slot retries; use the matching manual request after the provider recovers, or let the next scheduled slot try again.";
  if (normalized.includes("fmp_api_key") || normalized.includes("credential") || normalized.includes("unauthorized")) return "The worker could not authenticate. Verify FMP_API_KEY is available to the refresh container before retrying.";
  if (normalized.includes("identity") || normalized.includes("version") || normalized.includes("revision")) return "The service and refresh images must come from the same release. Redeploy the matching image pair before retrying.";
  return "The worker exhausted its retries for this slot. Review the reported reason and worker log, correct the underlying issue, then use the matching manual request or wait for the next automatic run.";
}

function refreshActivity(state: string | undefined, session: string | undefined, message: string | undefined, premarketActive: boolean, afterCloseActive: boolean, submitting: boolean): { title: string; session: string; message: string } {
  if (submitting) return { title: "Submitting refresh request", session: "Manual request", message: "The service is writing a request for the credentialed worker." };
  if (state === "queued") return { title: "Refresh queued", session: sessionLabel(session), message: message || "Waiting for the current cache refresh to finish." };
  if (state === "running") return { title: "Manual refresh running", session: sessionLabel(session), message: message || "The worker is collecting and validating cached market data." };
  if (premarketActive) return { title: "Morning collection running", session: "Scheduled premarket", message: "The scheduled worker is updating Focus Desk premarket evidence." };
  if (afterCloseActive) return { title: "Scheduled maintenance running", session: "Weekday maintenance", message: "This run refreshes Investments, Economics, and completed Swing Trading market data. No manual request is needed." };
  return { title: "Refresh activity", session: "Worker", message: "The worker is processing cached market data." };
}

function sessionLabel(session?: string): string {
  if (session === "premarket") return "Premarket";
  if (session === "after-close") return "Completed bars";
  if (session === "universe-backfill") return "Universe backfill";
  return "Manual request";
}
